import { apiService } from './api';
import { errorHandlerService } from './errorHandlerService';
import i18n from '../i18n';
import {
  SubscriptionPlan,
  XpSubscriptionOffer,
  XpRedemptionPreview,
  XpRedemptionStackingMode,
  XpRedemptionResult,
  XpRedemptionHistory,
  UserSubscriptionStatus,
  PaymentDetails,
  PaymentResult,
  SubscriptionHistory,
  PaymentHistoryItem,
  PaymentDetails_API,
  ContentAccessResponse,
  SubscriptionError,
  TimeFormatResult,
  ProgressCalculation,
  SubscriptionActivityItem
} from '../types/subscription';
import { normalizeUserSubscriptionStatus } from '../utils/normalizeSubscriptionStatus';

function normalizeStackingMode(value?: string): XpRedemptionStackingMode {
  if (value === 'extend' || value === 'switch' || value === 'new') return value;
  return 'new';
}

function normalizeXpRedemptionResult(raw: Record<string, unknown>): XpRedemptionResult {
  const remaining =
    (raw.remainingXp as number | undefined) ??
    (raw.RemainingXp as number | undefined) ??
    (raw.remainingXP as number | undefined);
  return {
    success: Boolean(raw.success ?? raw.Success ?? false),
    message: String(raw.message ?? raw.Message ?? ''),
    xpSpent: Number(raw.xpSpent ?? raw.XpSpent ?? 0),
    remainingXp: typeof remaining === 'number' ? remaining : undefined,
    planName: String(raw.planName ?? raw.PlanName ?? ''),
    grantDurationDays: Number(raw.grantDurationDays ?? raw.GrantDurationDays ?? 0),
    subscriptionEndDate: String(raw.subscriptionEndDate ?? raw.SubscriptionEndDate ?? ''),
    redemptionId: (raw.redemptionId ?? raw.RedemptionId) as number | undefined,
    stackingMode: normalizeStackingMode((raw.stackingMode ?? raw.StackingMode) as string | undefined),
    effectSummary: String(raw.effectSummary ?? raw.EffectSummary ?? ''),
  };
}

function extractRedemptionErrorBody(error: unknown): Record<string, unknown> | null {
  if (!error || typeof error !== 'object') return null;
  const err = error as Record<string, unknown>;
  if ('success' in err || 'Success' in err) return err;
  const candidate = err.details ?? err.responseData ?? (err as { response?: { data?: unknown } }).response?.data;
  if (candidate && typeof candidate === 'object' && ('success' in candidate || 'Success' in candidate)) {
    return candidate as Record<string, unknown>;
  }
  return null;
}

function normalizeXpOfferForLearner(raw: XpSubscriptionOffer & { preview?: Record<string, unknown> }): XpSubscriptionOffer {
  const p = raw.preview as Record<string, unknown> | undefined;
  let preview: XpRedemptionPreview | undefined;
  if (p) {
    preview = {
      stackingMode: normalizeStackingMode((p.stackingMode ?? p.StackingMode) as string | undefined),
      effectSummary: String(p.effectSummary ?? p.EffectSummary ?? ''),
      detailLabel: String(p.detailLabel ?? p.DetailLabel ?? ''),
      confirmMessage: String(p.confirmMessage ?? p.ConfirmMessage ?? ''),
      projectedEndDate: String(p.projectedEndDate ?? p.ProjectedEndDate ?? ''),
      currentPlanName: (p.currentPlanName ?? p.CurrentPlanName) as string | undefined,
      currentPlanEndDate: (p.currentPlanEndDate ?? p.CurrentPlanEndDate) as string | undefined,
      replacedPlanNames: (p.replacedPlanNames ?? p.ReplacedPlanNames) as string[] | undefined,
    };
  }
  const r = raw as XpSubscriptionOffer & Record<string, unknown>;
  const name = String(r.name ?? r.Name ?? '').trim();
  return {
    ...raw,
    name: name || raw.subscriptionPlanName || 'XP Offer',
    preview,
  };
}

function normalizeSubscriptionPlan(raw: Record<string, unknown>): SubscriptionPlan {
  const duration = Number(raw.duration ?? raw.Duration ?? raw.durationDays ?? raw.DurationDays ?? 0);
  return {
    id: Number(raw.id ?? raw.Id ?? 0),
    name: String(raw.name ?? raw.Name ?? ''),
    duration: Number.isFinite(duration) && duration > 0 ? Math.round(duration) : 0,
    price: Number(raw.price ?? raw.Price ?? 0),
    description: String(raw.description ?? raw.Description ?? '').trim() || undefined,
    icon: (raw.icon ?? raw.Icon) as string | undefined,
    color: (raw.color ?? raw.Color) as string | undefined,
    isActive: Boolean(raw.isActive ?? raw.IsActive ?? true),
    createdAt: String(raw.createdAt ?? raw.CreatedAt ?? ''),
  };
}

function normalizeXpRedemptionHistoryRow(row: XpRedemptionHistory): XpRedemptionHistory {
  const r = row as XpRedemptionHistory & Record<string, unknown>;
  const before = r.xpBalanceBefore ?? (r.XpBalanceBefore as number | undefined);
  const after = r.xpBalanceAfter ?? (r.XpBalanceAfter as number | undefined);
  const offerName = (r.offerName ?? r.OfferName) as string | undefined;
  return {
    ...row,
    offerName: offerName?.trim() || null,
    xpBalanceBefore: typeof before === 'number' ? before : null,
    xpBalanceAfter: typeof after === 'number' ? after : null,
  };
}

class SubscriptionService {
  // ============================================================================
  // SUBSCRIPTION PLAN METHODS
  // ============================================================================

  /**
   * Fetch all available subscription plans
   */
  async getPlans(): Promise<SubscriptionPlan[]> {
    try {
      const plans = await apiService.get<Record<string, unknown>[]>('/api/Subscription/plans');
      return (plans ?? []).map((plan) => normalizeSubscriptionPlan(plan));
    } catch (error) {
      const processedError = this.handleSubscriptionError(error, 'network', 'fetching subscription plans');
      this.logError(processedError, 'getPlans');
      throw processedError;
    }
  }

  // ============================================================================
  // XP REDEMPTION
  // ============================================================================

  async getXpOffers(): Promise<XpSubscriptionOffer[]> {
    try {
      const offers = await apiService.get<XpSubscriptionOffer[]>('/api/Subscription/xp-offers', { cache: false });
      return (offers || []).map(normalizeXpOfferForLearner);
    } catch (error) {
      const processedError = this.handleSubscriptionError(error, 'network', 'fetching XP offers');
      this.logError(processedError, 'getXpOffers');
      throw processedError;
    }
  }

  /** Offers with stacking preview (extend / switch / new) for the signed-in learner. */
  async getXpOffersForMe(): Promise<XpSubscriptionOffer[]> {
    try {
      const offers = await apiService.get<XpSubscriptionOffer[]>('/api/Subscription/xp-offers/for-me', {
        cache: false,
      });
      return (offers || []).map(normalizeXpOfferForLearner);
    } catch (error) {
      const processedError = this.handleSubscriptionError(error, 'network', 'fetching XP offers');
      this.logError(processedError, 'getXpOffersForMe');
      throw processedError;
    }
  }

  async redeemXpOffer(offerId: number): Promise<XpRedemptionResult> {
    try {
      const raw = await apiService.post<Record<string, unknown>>(`/api/Subscription/xp-redeem/${offerId}`, {});
      return normalizeXpRedemptionResult(raw);
    } catch (error: unknown) {
      const body = extractRedemptionErrorBody(error);
      if (body) {
        return normalizeXpRedemptionResult(body);
      }
      const processedError = this.handleSubscriptionError(error, 'validation', 'redeeming XP offer');
      this.logError(processedError, 'redeemXpOffer');
      throw processedError;
    }
  }

  async getXpRedemptionHistory(limit = 50): Promise<XpRedemptionHistory[]> {
    try {
      const rows = await apiService.get<XpRedemptionHistory[]>(
        `/api/Subscription/xp-redemptions/history?limit=${limit}&_=${Date.now()}`,
        { cache: false }
      );
      return (rows || []).map(normalizeXpRedemptionHistoryRow);
    } catch (error) {
      const processedError = this.handleSubscriptionError(error, 'network', 'fetching XP redemption history');
      this.logError(processedError, 'getXpRedemptionHistory');
      throw processedError;
    }
  }

  // ============================================================================
  // SUBSCRIPTION STATUS METHODS
  // ============================================================================

  /**
   * Get user's current subscription status
   */
  async getSubscriptionStatus(): Promise<UserSubscriptionStatus> {
    try {
      const status = await apiService.get<Record<string, unknown>>('/api/Subscription/status', { cache: false });
      return normalizeUserSubscriptionStatus(status);
    } catch (error) {
      const processedError = this.handleSubscriptionError(error, 'network', 'fetching subscription status');
      this.logError(processedError, 'getSubscriptionStatus');
      throw processedError;
    }
  }

  /**
   * Check if user has access to specific content
   */
  async checkContentAccess(): Promise<ContentAccessResponse> {
    try {
      const access = await apiService.get<ContentAccessResponse>('/api/Subscription/access');
      return access;
    } catch (error) {
      const processedError = this.handleSubscriptionError(error, 'network', 'checking content access');
      this.logError(processedError, 'checkContentAccess');
      throw processedError;
    }
  }

  // ============================================================================
  // PAYMENT PROCESSING METHODS
  // ============================================================================

  /**
   * Process subscription payment
   */
  async purchaseSubscription(paymentDetails: PaymentDetails): Promise<PaymentResult> {
    try {
      // Validate payment details before sending
      this.validatePaymentDetails(paymentDetails);

      const result = await apiService.post<PaymentResult>('/api/Subscription/purchase', paymentDetails);
      
      if (!result.success) {
        const error = this.createSubscriptionError(
          result.message || 'Payment processing failed',
          'payment',
          400,
          'Payment was declined or failed to process'
        );
        this.logError(error, 'purchaseSubscription');
        throw error;
      }

      // Log successful payment
      this.logPaymentSuccess(result, paymentDetails.planId);
      return result;
    } catch (error) {
      if (error instanceof Error && error.name === 'SubscriptionError') {
        throw error;
      }
      const processedError = this.handleSubscriptionError(error, 'payment', 'processing subscription payment');
      this.logError(processedError, 'purchaseSubscription');
      throw processedError;
    }
  }

  // ============================================================================
  // SUBSCRIPTION HISTORY METHODS
  // ============================================================================

  /**
   * Get user's subscription history
   */
  async getSubscriptionActivity(limit = 100): Promise<SubscriptionActivityItem[]> {
    try {
      const rows = await apiService.get<Record<string, unknown>[]>(
        `/api/Subscription/activity?limit=${Math.min(Math.max(limit, 1), 200)}`,
        { cache: false }
      );
      return (rows ?? []).map((row) => {
        const r = row as Record<string, unknown>;
        const type = String(r.activityType ?? r.ActivityType ?? 'subscription').toLowerCase();
        const activityType =
          type === 'xp_redeem' || type === 'purchase' || type === 'admin_assign' || type === 'subscription'
            ? type
            : 'subscription';
        return {
          id: String(r.id ?? r.Id ?? ''),
          activityType,
          title: String(r.title ?? r.Title ?? ''),
          detail: (r.detail ?? r.Detail) as string | undefined,
          planName: String(r.planName ?? r.PlanName ?? ''),
          planIcon: (r.planIcon ?? r.PlanIcon) as string | undefined,
          planColor: (r.planColor ?? r.PlanColor) as string | undefined,
          occurredAt: String(r.occurredAt ?? r.OccurredAt ?? ''),
          amount: (r.amount ?? r.Amount) as number | undefined,
          currency: (r.currency ?? r.Currency) as string | undefined,
          xpSpent: (r.xpSpent ?? r.XpSpent) as number | undefined,
          grantDurationDays: (r.grantDurationDays ?? r.GrantDurationDays) as number | undefined,
          offerName: (r.offerName ?? r.OfferName) as string | undefined,
          subscriptionStartDate: (r.subscriptionStartDate ?? r.SubscriptionStartDate) as string | undefined,
          subscriptionEndDate: (r.subscriptionEndDate ?? r.SubscriptionEndDate) as string | undefined,
          isSubscriptionActive: (r.isSubscriptionActive ?? r.IsSubscriptionActive) as boolean | undefined
        } as SubscriptionActivityItem;
      });
    } catch (error) {
      const processedError = this.handleSubscriptionError(error, 'network', 'fetching subscription activity');
      this.logError(processedError, 'getSubscriptionActivity');
      throw processedError;
    }
  }

  async getSubscriptionHistory(): Promise<SubscriptionHistory[]> {
    try {
      const history = await apiService.get<SubscriptionHistory[]>('/api/Subscription/history');
      return history || [];
    } catch (error) {
      const processedError = this.handleSubscriptionError(error, 'network', 'fetching subscription history');
      this.logError(processedError, 'getSubscriptionHistory');
      throw processedError;
    }
  }

  /**
   * Get user's payment history
   */
  async getPaymentHistory(): Promise<PaymentHistoryItem[]> {
    try {
      const history = await apiService.get<PaymentHistoryItem[]>('/api/Subscription/payments');
      return history || [];
    } catch (error) {
      const processedError = this.handleSubscriptionError(error, 'network', 'fetching payment history');
      this.logError(processedError, 'getPaymentHistory');
      throw processedError;
    }
  }

  /**
   * Get specific payment details
   */
  async getPaymentDetails(paymentId: string): Promise<PaymentDetails_API> {
    try {
      const details = await apiService.get<PaymentDetails_API>(`/api/Subscription/payments/${paymentId}`);
      return details;
    } catch (error) {
      const processedError = this.handleSubscriptionError(error, 'network', `fetching payment details for ${paymentId}`);
      this.logError(processedError, 'getPaymentDetails');
      throw processedError;
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Format remaining time for display
   */
  formatRemainingTime(days: number): TimeFormatResult {
    if (days <= 0) {
      return {
        value: 0,
        unit: 'days',
        display: 'Expired'
      };
    }

    if (days === 1) {
      return {
        value: 1,
        unit: 'day',
        display: '1 day'
      };
    }

    if (days < 30) {
      return {
        value: days,
        unit: 'days',
        display: `${days} days`
      };
    }

    if (days < 365) {
      const months = Math.floor(days / 30);
      const remainingDays = days % 30;
      
      if (remainingDays === 0) {
        return {
          value: months,
          unit: months === 1 ? 'month' : 'months',
          display: `${months} ${months === 1 ? 'month' : 'months'}`
        };
      }
      
      return {
        value: months,
        unit: 'months',
        display: `${months} ${months === 1 ? 'month' : 'months'}, ${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`
      };
    }

    const years = Math.floor(days / 365);
    const remainingDays = days % 365;
    
    if (remainingDays === 0) {
      return {
        value: years,
        unit: years === 1 ? 'year' : 'years',
        display: `${years} ${years === 1 ? 'year' : 'years'}`
      };
    }

    const months = Math.floor(remainingDays / 30);
    if (months > 0) {
      return {
        value: years,
        unit: 'years',
        display: `${years} ${years === 1 ? 'year' : 'years'}, ${months} ${months === 1 ? 'month' : 'months'}`
      };
    }

    return {
      value: years,
      unit: 'years',
      display: `${years} ${years === 1 ? 'year' : 'years'}, ${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`
    };
  }

  /**
   * Calculate subscription progress percentage
   */
  calculateProgress(startDate: string, endDate: string): ProgressCalculation {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const now = Date.now();

    const total = end - start;
    const elapsed = Math.max(0, now - start);
    const remaining = Math.max(0, end - now);

    const percentage = total > 0 ? Math.min(100, Math.max(0, (elapsed / total) * 100)) : 0;

    return {
      percentage: Math.round(percentage * 100) / 100, // Round to 2 decimal places
      elapsed: Math.ceil(elapsed / (1000 * 60 * 60 * 24)), // Convert to days
      remaining: Math.ceil(remaining / (1000 * 60 * 60 * 24)), // Convert to days
      total: Math.ceil(total / (1000 * 60 * 60 * 24)) // Convert to days
    };
  }

  /**
   * Format plan duration for display
   */
  formatPlanDuration(days: number): string {
    const d = Math.round(Number(days));
    if (!Number.isFinite(d) || d <= 0) {
      return '—';
    }

    if (d === 30) return '1 Month';
    if (d === 60) return '2 Months';
    if (d === 90) return '3 Months';
    if (d === 180) return '6 Months';
    if (d === 365) return '1 Year';
    
    if (d < 30) {
      return `${d} ${d === 1 ? 'Day' : 'Days'}`;
    }
    
    if (d < 365) {
      const months = Math.floor(d / 30);
      const remainingDays = d % 30;
      
      if (remainingDays === 0) {
        return `${months} ${months === 1 ? 'Month' : 'Months'}`;
      }
      
      return `${months} ${months === 1 ? 'Month' : 'Months'}, ${remainingDays} ${remainingDays === 1 ? 'Day' : 'Days'}`;
    }
    
    const years = Math.floor(d / 365);
    const remainingDays = d % 365;
    
    if (remainingDays === 0) {
      return `${years} ${years === 1 ? 'Year' : 'Years'}`;
    }
    
    const months = Math.floor(remainingDays / 30);
    const extraDays = remainingDays % 30;
    if (extraDays === 0) {
      return `${years} ${years === 1 ? 'Year' : 'Years'}, ${months} ${months === 1 ? 'Month' : 'Months'}`;
    }

    return `${years} ${years === 1 ? 'Year' : 'Years'}, ${months} ${months === 1 ? 'Month' : 'Months'}, ${extraDays} ${extraDays === 1 ? 'Day' : 'Days'}`;
  }

  /**
   * Format card number for display (with spaces)
   */
  formatCardNumber(cardNumber: string): string {
    // Remove all non-digit characters
    const cleaned = cardNumber.replace(/\D/g, '');
    
    // Add spaces every 4 digits
    const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
    
    // Limit to 19 characters (16 digits + 3 spaces)
    return formatted.substring(0, 19);
  }

  /**
   * Validate card number format
   */
  validateCardNumber(cardNumber: string): boolean {
    const cleaned = cardNumber.replace(/\D/g, '');
    
    // Check length (13-19 digits for various card types)
    if (cleaned.length < 13 || cleaned.length > 19) {
      return false;
    }
    
    // Luhn algorithm validation
    let sum = 0;
    let isEven = false;
    
    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned.charAt(i), 10);
      
      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }
      
      sum += digit;
      isEven = !isEven;
    }
    
    return sum % 10 === 0;
  }

  /**
   * Validate expiry date
   */
  validateExpiryDate(month: number, year: number): boolean {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11
    
    // Check valid month
    if (month < 1 || month > 12) {
      return false;
    }
    
    // Check year is not in the past
    if (year < currentYear) {
      return false;
    }
    
    // If same year, check month is not in the past
    if (year === currentYear && month < currentMonth) {
      return false;
    }
    
    // Check year is not too far in the future (20 years)
    if (year > currentYear + 20) {
      return false;
    }
    
    return true;
  }

  /**
   * Validate CVV
   */
  validateCVV(cvv: string): boolean {
    const cleaned = cvv.replace(/\D/g, '');
    return cleaned.length >= 3 && cleaned.length <= 4;
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * Validate payment details before submission
   */
  private validatePaymentDetails(paymentDetails: PaymentDetails): void {
    const errors: string[] = [];

    // Validate card number
    if (!this.validateCardNumber(paymentDetails.cardNumber)) {
      errors.push('Invalid card number');
    }

    // Validate expiry date
    if (!this.validateExpiryDate(paymentDetails.expiryMonth, paymentDetails.expiryYear)) {
      errors.push('Invalid expiry date');
    }

    // Validate CVV
    if (!this.validateCVV(paymentDetails.cvv)) {
      errors.push('Invalid CVV');
    }

    // Validate cardholder name
    if (!paymentDetails.cardHolderName || paymentDetails.cardHolderName.trim().length < 2) {
      errors.push('Cardholder name is required');
    }

    // Validate plan ID
    if (!paymentDetails.planId || paymentDetails.planId <= 0) {
      errors.push('Invalid plan selected');
    }

    if (errors.length > 0) {
      throw this.createSubscriptionError(
        `Payment validation failed: ${errors.join(', ')}`,
        'validation',
        400,
        errors.join('; ')
      );
    }
  }

  /**
   * Handle subscription-specific errors with enhanced error processing
   */
  private handleSubscriptionError(error: any, type: SubscriptionError['type'], context?: string): SubscriptionError {
    let message = 'An unexpected error occurred';
    let statusCode = 500;
    let details = '';

    // Extract error information
    if (error?.message) {
      message = error.message;
    }
    if (error?.statusCode) {
      statusCode = error.statusCode;
    }
    if (error?.details) {
      details = error.details;
    }

    // Handle specific error types with detailed messages
    switch (type) {
      case 'payment':
        message = this.getPaymentErrorMessage(statusCode, error);
        break;
      case 'network':
        message = this.getNetworkErrorMessage(statusCode, error);
        break;
      case 'validation':
        // Validation errors should already have appropriate messages
        break;
      case 'server':
        message = 'Server error occurred. Our team has been notified. Please try again later.';
        break;
      case 'auth':
        message = this.getAuthErrorMessage(statusCode, error);
        break;
    }

    const subscriptionError = this.createSubscriptionError(message, type, statusCode, details);
    
    // Add context information
    if (context) {
      subscriptionError.context = context;
    }

    return subscriptionError;
  }

  /**
   * Get specific payment error messages
   */
  private getPaymentErrorMessage(statusCode: number, error: any): string {
    switch (statusCode) {
      case 400:
        if (error?.message?.includes('card')) {
          return 'Invalid card information. Please check your card details and try again.';
        }
        return 'Payment processing failed. Please verify your payment information and try again.';
      case 402:
        return 'Payment was declined by your bank. Please try a different payment method or contact your bank.';
      case 403:
        return 'Payment not authorized. Please verify your card details or try a different payment method.';
      case 409:
        return 'A payment for this subscription is already being processed. Please wait a moment and try again.';
      case 422:
        return 'Invalid payment information provided. Please check all fields and try again.';
      case 429:
        return 'Too many payment attempts. Please wait 15 minutes before trying again.';
      case 503:
        return 'Payment processing is temporarily unavailable. Please try again in a few minutes.';
      default:
        return 'Payment processing failed. Please try again or contact support if the problem persists.';
    }
  }

  /**
   * Get specific network error messages
   */
  private getNetworkErrorMessage(statusCode: number, error: any): string {
    switch (statusCode) {
      case 0:
      case 408:
        return 'Connection timeout. Please check your internet connection and try again.';
      case 401:
        return i18n.t('sessionExpired', { ns: 'auth' });
      case 403:
        return 'Access denied. Please check your subscription status or contact support.';
      case 404:
        return 'The requested service is not available. Please try again later.';
      case 429:
        return 'Too many requests. Please wait a moment before trying again.';
      case 500:
        return 'Server error occurred. Our team has been notified. Please try again later.';
      case 502:
      case 503:
        return 'Service is temporarily unavailable. Please try again in a few minutes.';
      case 504:
        return 'Server response timeout. Please try again.';
      default:
        if (!navigator.onLine) {
          return 'No internet connection. Please check your network and try again.';
        }
        return 'Network error occurred. Please check your connection and try again.';
    }
  }

  /**
   * Get specific authentication error messages
   */
  private getAuthErrorMessage(statusCode: number, error: any): string {
    switch (statusCode) {
      case 401:
        return 'Authentication required. Please log in to access subscription features.';
      case 403:
        return 'Access denied. You do not have permission to perform this action.';
      default:
        return 'Authentication failed. Please log in and try again.';
    }
  }

  /**
   * Create a subscription-specific error
   */
  private createSubscriptionError(
    message: string,
    type: SubscriptionError['type'],
    statusCode: number,
    details?: string
  ): SubscriptionError {
    const error = new Error(message) as SubscriptionError;
    error.name = 'SubscriptionError';
    error.type = type;
    error.statusCode = statusCode;
    error.details = details || undefined;
    error.timestamp = new Date().toISOString();
    error.retryable = this.isErrorRetryable({ statusCode, type }, [408, 429, 500, 502, 503, 504]);
    return error;
  }

  /**
   * Log subscription errors for monitoring and debugging
   */
  private logError(error: SubscriptionError, method: string): void {
    const errorData = {
      timestamp: new Date().toISOString(),
      method,
      type: error.type,
      message: error.message,
      statusCode: error.statusCode,
      details: error.details,
      context: (error as any).context,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId()
    };

    // Log to console for development
    console.error('Subscription Error:', errorData);

    // Store locally for offline scenarios
    this.storeErrorLocally(errorData);

    // Report to error logging service
    this.reportErrorToService(errorData);
  }

  /**
   * Log successful payment for analytics
   */
  private logPaymentSuccess(result: PaymentResult, planId: number): void {
    const successData = {
      timestamp: new Date().toISOString(),
      event: 'payment_success',
      transactionId: result.transactionId,
      planId,
      subscriptionEndDate: result.subscriptionEndDate,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId()
    };

    console.log('Payment Success:', successData);
    this.storeSuccessLocally(successData);
  }

  /**
   * Store error data locally for offline scenarios
   */
  private storeErrorLocally(errorData: any): void {
    try {
      const errors = JSON.parse(localStorage.getItem('subscriptionErrors') || '[]');
      errors.push(errorData);
      
      // Keep only last 50 errors
      if (errors.length > 50) {
        errors.splice(0, errors.length - 50);
      }
      
      localStorage.setItem('subscriptionErrors', JSON.stringify(errors));
    } catch (error) {
      console.warn('Failed to store subscription error locally:', error);
    }
  }

  /**
   * Store success data locally for analytics
   */
  private storeSuccessLocally(successData: any): void {
    try {
      const successes = JSON.parse(localStorage.getItem('subscriptionSuccesses') || '[]');
      successes.push(successData);
      
      // Keep only last 20 successes
      if (successes.length > 20) {
        successes.splice(0, successes.length - 20);
      }
      
      localStorage.setItem('subscriptionSuccesses', JSON.stringify(successes));
    } catch (error) {
      console.warn('Failed to store subscription success locally:', error);
    }
  }

  /**
   * Report error to monitoring service
   */
  private async reportErrorToService(errorData: any): Promise<void> {
    try {
      await fetch('/api/LogManagement/error-logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          level: 'error',
          message: errorData.message || 'Subscription service error',
          source: 'SubscriptionService',
          stackTrace: errorData.stack || JSON.stringify(errorData),
          userId: errorData.userId,
          timestamp: new Date().toISOString(),
          resolved: false,
          tags: ['subscription-service', 'subscription-error']
        }),
      });
    } catch (error) {
      console.warn('Failed to report subscription error to LogManagement API:', error);
    }
  }

  /**
   * Get current user ID for error tracking
   */
  private getCurrentUserId(): string | undefined {
    try {
      const user = JSON.parse(localStorage.getItem('userProfile') || '{}');
      return user.id;
    } catch {
      return undefined;
    }
  }

  /**
   * Get session ID for error tracking
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Get authentication token
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  // ============================================================================
  // RETRY MECHANISM
  // ============================================================================

  /**
   * Retry failed requests with exponential backoff and enhanced error handling
   */
  private async retryRequest<T>(
    requestFn: () => Promise<T>,
    context: string,
    maxRetries: number = 3,
    baseDelay: number = 1000
  ): Promise<T> {
    let lastError: any;
    const retryableStatusCodes = [408, 429, 500, 502, 503, 504];

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await requestFn();
        
        // Log successful retry if this wasn't the first attempt
        if (attempt > 0) {
          console.log(`Retry successful for ${context} after ${attempt} attempts`);
        }
        
        return result;
      } catch (error: any) {
        lastError = error;

        // Check if error is retryable
        const isRetryable = this.isErrorRetryable(error, retryableStatusCodes);
        
        // Don't retry non-retryable errors
        if (!isRetryable) {
          console.log(`Non-retryable error for ${context}:`, error.statusCode);
          throw error;
        }

        // Don't retry on the last attempt
        if (attempt === maxRetries) {
          console.log(`Max retries (${maxRetries}) reached for ${context}`);
          break;
        }

        // Calculate delay with exponential backoff and jitter
        const delay = this.calculateRetryDelay(baseDelay, attempt);
        console.log(`Retrying ${context} in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    // Enhance the final error with retry information
    if (lastError) {
      lastError.retryAttempts = maxRetries;
      lastError.context = context;
    }

    throw lastError;
  }

  /**
   * Check if an error is retryable
   */
  private isErrorRetryable(error: any, retryableStatusCodes: number[]): boolean {
    // Network errors are generally retryable
    if (!error.statusCode) {
      return true;
    }

    // Check against retryable status codes
    if (retryableStatusCodes.includes(error.statusCode)) {
      return true;
    }

    // Don't retry client errors (4xx) except specific cases
    if (error.statusCode >= 400 && error.statusCode < 500) {
      return false;
    }

    // Retry server errors (5xx)
    return error.statusCode >= 500;
  }

  /**
   * Calculate retry delay with exponential backoff and jitter
   */
  private calculateRetryDelay(baseDelay: number, attempt: number): number {
    // Exponential backoff: baseDelay * 2^attempt
    const exponentialDelay = baseDelay * Math.pow(2, attempt);
    
    // Add jitter to prevent thundering herd (±25% random variation)
    const jitter = exponentialDelay * 0.25 * (Math.random() - 0.5);
    
    // Cap maximum delay at 30 seconds
    return Math.min(exponentialDelay + jitter, 30000);
  }

  /**
   * Get subscription status with retry
   */
  async getSubscriptionStatusWithRetry(): Promise<UserSubscriptionStatus> {
    return this.retryRequest(
      () => this.getSubscriptionStatus(),
      'getSubscriptionStatus'
    );
  }

  /**
   * Get plans with retry
   */
  async getPlansWithRetry(): Promise<SubscriptionPlan[]> {
    return this.retryRequest(
      () => this.getPlans(),
      'getPlans'
    );
  }

  /**
   * Get subscription history with retry
   */
  async getSubscriptionHistoryWithRetry(): Promise<SubscriptionHistory[]> {
    return this.retryRequest(
      () => this.getSubscriptionHistory(),
      'getSubscriptionHistory'
    );
  }

  /**
   * Get payment history with retry
   */
  async getPaymentHistoryWithRetry(): Promise<PaymentHistoryItem[]> {
    return this.retryRequest(
      () => this.getPaymentHistory(),
      'getPaymentHistory'
    );
  }

  /**
   * Check content access with retry
   */
  async checkContentAccessWithRetry(): Promise<ContentAccessResponse> {
    return this.retryRequest(
      () => this.checkContentAccess(),
      'checkContentAccess'
    );
  }

  // ============================================================================
  // ERROR RECOVERY AND ADMIN METHODS
  // ============================================================================

  /**
   * Get error statistics for admin monitoring
   */
  getErrorStatistics(): {
    totalErrors: number;
    errorsByType: Record<string, number>;
    recentErrors: any[];
    errorRate: number;
  } {
    try {
      const errors = JSON.parse(localStorage.getItem('subscriptionErrors') || '[]');
      const successes = JSON.parse(localStorage.getItem('subscriptionSuccesses') || '[]');
      
      const errorsByType: Record<string, number> = {};
      errors.forEach((error: any) => {
        errorsByType[error.type] = (errorsByType[error.type] || 0) + 1;
      });

      // Calculate error rate (errors / total operations)
      const totalOperations = errors.length + successes.length;
      const errorRate = totalOperations > 0 ? (errors.length / totalOperations) * 100 : 0;

      return {
        totalErrors: errors.length,
        errorsByType,
        recentErrors: errors.slice(-10),
        errorRate: Math.round(errorRate * 100) / 100
      };
    } catch {
      return {
        totalErrors: 0,
        errorsByType: {},
        recentErrors: [],
        errorRate: 0
      };
    }
  }

  /**
   * Clear error logs
   */
  clearErrorLogs(): void {
    localStorage.removeItem('subscriptionErrors');
    localStorage.removeItem('subscriptionSuccesses');
  }

  /**
   * Test network connectivity
   */
  async testConnectivity(): Promise<boolean> {
    try {
      const response = await fetch('/api/health', {
        method: 'HEAD',
        cache: 'no-cache'
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Get network status information
   */
  getNetworkStatus(): {
    online: boolean;
    connectionType?: string;
    effectiveType?: string;
  } {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    return {
      online: navigator.onLine,
      connectionType: connection?.type,
      effectiveType: connection?.effectiveType
    };
  }
}

export const subscriptionService = new SubscriptionService();
export default subscriptionService;